import { ref, readonly, onMounted, onUnmounted, watch } from 'vue';
import { useAudioPlayer } from './useAudioPlayer';
import { useSdk } from '@/composables/useSdk';
import { audioViewTracker } from '@/services/audioViewTracker';
import type { Post } from '@/types';

export function useAudioViewTracking() {
  const audioPlayer = useAudioPlayer();
  const { sdk } = useSdk();
  const isTracking = ref(false);
  const currentTrackUlid = ref<string | null>(null);

  // Initialize audio view tracker with SDK
  onMounted(() => {
    audioViewTracker.initialize(sdk);
  });

  // Watch for current track changes
  watch(
    () => audioPlayer.currentTrack.value,
    (newTrack, oldTrack) => {
      // Stop tracking old track
      if (oldTrack?.ulid) {
        isTracking.value = false;
        currentTrackUlid.value = null;
      }

      // Start tracking new track if it has a ULID
      if (newTrack?.ulid) {
        currentTrackUlid.value = newTrack.ulid;

        // Start tracking if currently playing
        if (audioPlayer.isPlaying.value) {
          isTracking.value = true;
        }
      }
    },
    { immediate: true }
  );

  // Watch for play/pause state changes
  watch(
    () => audioPlayer.isPlaying.value,
    (playing) => {
      if (currentTrackUlid.value && audioPlayer.currentTrack.value) {
        isTracking.value = playing;
      }
    }
  );

  // Watch for time updates and track progress
  watch(
    () => audioPlayer.currentTime.value,
    (currentTime) => {
      if (
        isTracking.value &&
        audioPlayer.currentTrack.value &&
        audioPlayer.duration.value > 0
      ) {
        audioViewTracker.trackTimeUpdate(
          audioPlayer.currentTrack.value,
          currentTime,
          audioPlayer.duration.value
        );
      }
    }
  );

  // Create manual tracking methods for direct integration
  const startTracking = () => {
    if (audioPlayer.currentTrack.value?.ulid) {
      isTracking.value = true;
    }
  };

  const stopTracking = () => {
    isTracking.value = false;
  };

  const onSeek = (newTime: number) => {
    if (audioPlayer.currentTrack.value) {
      audioViewTracker.onSeek(audioPlayer.currentTrack.value, newTime);
    }
  };

  // Manual time update for custom players
  const onTimeUpdate = (currentTime: number, duration?: number) => {
    if (
      isTracking.value &&
      audioPlayer.currentTrack.value &&
      (duration || audioPlayer.duration.value) > 0
    ) {
      audioViewTracker.trackTimeUpdate(
        audioPlayer.currentTrack.value,
        currentTime,
        duration || audioPlayer.duration.value
      );
    }
  };

  // Track when user manually plays/pauses
  const onPlay = () => {
    startTracking();
  };

  const onPause = () => {
    stopTracking();
  };

  // Cleanup on component unmount
  onUnmounted(() => {
    isTracking.value = false;
  });

  return {
    // State
    isTracking: readonly(isTracking),
    currentTrackUlid: readonly(currentTrackUlid),

    // Methods for manual integration
    startTracking,
    stopTracking,
    onTimeUpdate,
    onSeek,
    onPlay,
    onPause,

    // Expose audio player methods
    playAudio: audioPlayer.togglePlay,
    stopAudio: audioPlayer.stop,
    seekTo: audioPlayer.seek,
    setVolume: audioPlayer.setVolume,

    // View tracker debugging methods
    getPendingViewsCount: audioViewTracker.getPendingViewsCount.bind(audioViewTracker),
    getTrackedPosts: audioViewTracker.getTrackedPosts.bind(audioViewTracker),
  };
}

// Helper for manual integration with custom audio players
export function useManualAudioViewTracking(post: Post) {
  const { sdk } = useSdk();
  const isTracking = ref(false);
  const lastTrackedTime = ref(0);

  onMounted(() => {
    audioViewTracker.initialize(sdk);
  });

  const onTimeUpdate = (currentTime: number, duration: number) => {
    if (!isTracking.value || !post.ulid) return;

    const currentSecond = Math.floor(currentTime);
    const lastSecond = Math.floor(lastTrackedTime.value);

    // Track if we've moved to a new 5-second segment
    if (Math.floor(currentSecond / 5) !== Math.floor(lastSecond / 5)) {
      const segmentStart = Math.floor(lastSecond / 5) * 5;
      const segmentEnd = Math.min(segmentStart + 4, Math.floor(duration));

      if (segmentEnd >= segmentStart) {
        audioViewTracker.trackSegment(post.ulid, segmentStart, segmentEnd, currentSecond);
      }
    }

    lastTrackedTime.value = currentTime;
  };

  const onSeek = (newTime: number) => {
    lastTrackedTime.value = newTime;
  };

  const onPlay = () => {
    isTracking.value = true;
  };

  const onPause = () => {
    isTracking.value = false;
  };

  const startTracking = () => {
    isTracking.value = true;
  };

  const stopTracking = () => {
    isTracking.value = false;
  };

  // Cleanup on component unmount
  onUnmounted(() => {
    stopTracking();
  });

  return {
    // State
    isTracking: readonly(isTracking),

    // Event handlers
    onTimeUpdate,
    onSeek,
    onPlay,
    onPause,

    // Manual controls
    startTracking,
    stopTracking,
  };
}
